import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { NumericFormat } from "react-number-format";
import styles from "./guest-bill-payment.module.scss";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { CustomerLoader, PaymentProcessor, PhoneInput, SuccessScreen, TransactionResultPage } from "../components";
import PaymentMethodModal, { PaymentMethodKey, RecapRow } from "../components/payment-method-modal/PaymentMethodModal";
import Button from "../../../common/ekedc-components/button/Button";
import { useGetGuestBillQuery, useInitiateGuestPayBillMutation } from "../../../service/vending-service";
import { getTransactionRefFromToken } from "../../../guards/common/authentification-functions";
import { RootState } from "../../../stores/store";
import { clearGuestBillPayment, setGuestBillPayment } from "../../../stores/slices/guest-bill-payment-slice";
import { getErrorMessage } from "../../../utils/error-handler";
import { formatCurrency } from "../../../utils";
import { validatePhoneNumber } from "../validators";
import { FailedTransactionIcon } from "../../../assets/icons";
import { ApplicationRoutes } from "../../../routes";
import successGif from "../../../assets/gifs/success-gif.gif";
import RouteDoesntExist from "../../../fallbacks/RouteDoesntExist";
import { resolveUtilityName } from "../../../tenant/branding/resolveTenant";
import { useTenant } from "../../../tenant/branding/useTenant";
import { getCurrencySymbol, getTenantCurrency } from "../../../tenant/currency";
import { FeatureFlagName } from "../../../tenant/features/featureFlags";
import { useFeatureFlags } from "../../../tenant/features/useFeatureFlag";

type GuestBillPaymentView = "form" | "redirecting" | "processing" | "success" | "failed";

interface GuestBillPaymentFormValues {
	email: string;
	phoneNumber: string;
	amount: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const GuestBillPaymentPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { token } = useParams<{ token: string }>();
	const [searchParams] = useSearchParams();
	const guestPayment = useSelector((state: RootState) => state.guestBillPayment);
	const { key: tenantKey, countryCode } = useTenant();
	const { isEnabled, isLoading: isFlagsLoading } = useFeatureFlags();
	// AEDC has its own guest payment flow; every other utility with the flag uses this page
	const isAvailable = isEnabled(FeatureFlagName.GUEST_BILLING) && tenantKey !== "aedc";
	const utilityName = resolveUtilityName();
	const currency = getTenantCurrency();

	// Payment Hub return: ?token=<jwt> or ?reference=...
	const returnReference = useMemo(() => {
		const hubToken = searchParams.get("token");
		if (hubToken) return getTransactionRefFromToken(hubToken) ?? null;
		return searchParams.get("reference");
	}, [searchParams]);
	const isReturn = !!returnReference && guestPayment.token === token && !!guestPayment.guestCustomerDetailsId;

	const [view, setView] = useState<GuestBillPaymentView>(isReturn ? "processing" : "form");
	const [showMethodModal, setShowMethodModal] = useState(false);
	const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

	const { data: bill, isLoading, isError } = useGetGuestBillQuery({ token: token ?? "", utilityName }, { skip: !token || !isAvailable });
	const [initiateGuestPayBill, { isLoading: isInitiating }] = useInitiateGuestPayBillMutation();

	const {
		control,
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<GuestBillPaymentFormValues>({
		defaultValues: { email: "", phoneNumber: "", amount: "" },
	});

	useEffect(() => {
		if (bill) setValue("amount", bill.amountPayable.toString());
	}, [bill, setValue]);

	const goToSignIn = () => {
		dispatch(clearGuestBillPayment());
		navigate(`/${ApplicationRoutes.CUSTOMER_SIGNIN}`);
	};

	const handlePay = async (method: PaymentMethodKey) => {
		if (!token || isInitiating) return;
		const values = getValues();
		const amount = Number.parseFloat(values.amount);
		try {
			const response = await initiateGuestPayBill({
				token,
				utilityName,
				email: values.email.trim(),
				phoneNumber: values.phoneNumber || undefined,
				amount,
				returnUrl: `${window.location.origin}/${ApplicationRoutes.GUEST_BILL_PAYMENT}/${token}`,
				paymentTypes: method === "transfer" ? ["OneTimeWallet"] : ["Card"],
			}).unwrap();
			dispatch(
				setGuestBillPayment({
					token,
					guestCustomerDetailsId: response.guestCustomerDetailsId,
					amount,
					paymentMethod: t(method === "transfer" ? "buyElectricity.web.payWithTransfer" : "buyElectricity.web.debitCreditCard"),
				})
			);
			setShowMethodModal(false);
			setPaymentUrl(response.paymentUrl);
			setView("redirecting");
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data) ?? t("payment.paymentFailed"));
		}
	};

	if (isFlagsLoading) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<CustomerLoader />
			</CustomerPageLayout>
		);
	}

	if (!isAvailable) {
		return (
			<div className={styles.notFound}>
				<RouteDoesntExist />
			</div>
		);
	}

	if (view === "redirecting" && paymentUrl) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<PaymentProcessor paymentUrl={paymentUrl} themed />
			</CustomerPageLayout>
		);
	}

	if (view === "processing" && returnReference && guestPayment.guestCustomerDetailsId) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<PaymentProcessor
					reference={returnReference}
					guestCustomerDetailsId={guestPayment.guestCustomerDetailsId}
					skipRedirect
					themed
					onSuccess={() => setView("success")}
					onFailed={() => setView("failed")}
				/>
			</CustomerPageLayout>
		);
	}

	if (view === "success") {
		return (
			<CustomerPageLayout showBackButton={false}>
				<SuccessScreen
					title={t("payment.paymentSuccessfull")}
					subtitle={t("guestBillPayment.receiptSentToEmail")}
					buttonText={t("common.actions.done")}
					imageSrc={successGif}
					onContinue={goToSignIn}
				/>
			</CustomerPageLayout>
		);
	}

	if (view === "failed") {
		const now = new Date();
		return (
			<CustomerPageLayout showBackButton={false}>
				<TransactionResultPage
					transaction={{
						status: "failed",
						reference: returnReference || t("buyElectricity.web.notAvailable"),
						amount: guestPayment.amount ?? 0,
						paymentMethod: guestPayment.paymentMethod || t("buyElectricity.web.notAvailable"),
						date: now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
						time: now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
					}}
					currency={currency}
					onRetry={() => setView("form")}
					onDone={goToSignIn}
				/>
			</CustomerPageLayout>
		);
	}

	if (!token || isError) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<SuccessScreen
					icon={<FailedTransactionIcon />}
					title={t("guestBillPayment.billDetails")}
					subtitle={t("guestBillPayment.failedToLoadBill")}
					buttonText={t("common.actions.done")}
					onContinue={goToSignIn}
				/>
			</CustomerPageLayout>
		);
	}

	if (isLoading || !bill) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<CustomerLoader />
			</CustomerPageLayout>
		);
	}

	const recap: RecapRow[] = [
		{ label: t("common.labels.meterType"), value: t("common.labels.postpaid") },
		{ label: t("history.meterName"), value: bill.customerName },
		{ label: t("common.labels.accountNumber"), value: bill.accountNumber },
		{ label: t("common.labels.meterAddress"), value: bill.address },
		{ label: t("history.period"), value: bill.period },
		{ label: t("common.labels.outstandingBalance"), value: formatCurrency(bill.outstandingBalance, { currency }) },
		{ label: t("common.labels.amount"), value: formatCurrency(bill.amountPayable, { currency }) },
	];
	const enteredAmount = Number.parseFloat(getValues("amount") || "0");
	const amountText = formatCurrency(enteredAmount, { currency, decimals: 0 });
	// Modal recap shows the amount being paid instead of the bill amount
	const modalRecap: RecapRow[] = [...recap.slice(0, -1), { label: t("common.labels.amount"), value: formatCurrency(enteredAmount, { currency }) }];

	return (
		<CustomerPageLayout title={t("guestBillPayment.billDetails")} showBackButton={false}>
			<form className={styles.form} onSubmit={handleSubmit(() => setShowMethodModal(true))}>
				<div className={styles.content}>
					<div className={styles.recapCard}>
						{recap.map(row => (
							<div className={styles.recapRow} key={row.label}>
								<span className={styles.recapLabel}>{row.label}</span>
								<span className={styles.recapValue}>{row.value}</span>
							</div>
						))}
					</div>

					<section className={`${styles.card} ${styles.cardOverflowVisible}`}>
						<header className={styles.cardHeader}>{t("guestBillPayment.detailsInfo")}</header>
						<div className={styles.cardBody}>
							<div className={styles.field}>
								<span className={styles.fieldLabel}>
									{t("account.email")}
									<span className={styles.asterisk}>*</span>
								</span>
								<Controller
									name="email"
									control={control}
									rules={{
										required: t("common.validation.fieldIsRequired"),
										pattern: { value: EMAIL_PATTERN, message: t("payment.invalidEmailAddress") },
									}}
									render={({ field }) => (
										<input
											type="email"
											className={styles.textInput}
											placeholder={t("guestBillPayment.emailPlaceholder")}
											value={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
								{errors.email && <p className={styles.errorText}>{errors.email.message}</p>}
								<p className={styles.hintText}>{t("guestBillPayment.emailHint")}</p>
							</div>

							<div className={styles.field}>
								<span className={styles.fieldLabel}>{t("guestBillPayment.phoneOptional")}</span>
								<Controller
									name="phoneNumber"
									control={control}
									rules={{ validate: value => !value || validatePhoneNumber(value) }}
									render={({ field }) => (
										<PhoneInput
											value={field.value}
											// Only the dial code means no number was entered
											onChange={(phone, dialCode) => field.onChange(phone === `+${dialCode}` ? "" : phone)}
											placeholder={t("auth.web.phonePlaceholder")}
											defaultCountry={countryCode}
											lockCountry
											error={errors.phoneNumber?.message}
											className={styles.compactPhoneInput}
										/>
									)}
								/>
							</div>
						</div>
					</section>

					<section className={styles.card}>
						<div className={styles.cardBody}>
							<div className={styles.field}>
								<span className={styles.fieldLabel}>
									{t("common.labels.amount")}
									<span className={styles.asterisk}>*</span>
								</span>
								<div className={styles.amountField}>
									<Controller
										name="amount"
										control={control}
										rules={{
											required: t("buyElectricity.web.amountRequired"),
											validate: value => Number.parseFloat(value) > 0 || t("buyElectricity.web.amountGreaterThanZero"),
										}}
										render={({ field }) => (
											<>
												<span className={styles.amountPrefix}>{getCurrencySymbol(currency)}</span>
												<NumericFormat
													value={field.value}
													onValueChange={values => field.onChange(values.value)}
													onBlur={field.onBlur}
													thousandSeparator=","
													decimalSeparator="."
													decimalScale={2}
													allowNegative={false}
													placeholder="0.00"
													inputMode="decimal"
													className={styles.amountInput}
												/>
												<span className={styles.amountSuffix}>{currency}</span>
											</>
										)}
									/>
								</div>
								{errors.amount && <p className={styles.errorText}>{errors.amount.message}</p>}
							</div>
						</div>
					</section>
				</div>

				<footer className={styles.footer}>
					<Button type="submit" text={t("common.actions.continueKey")} />
				</footer>
			</form>

			{showMethodModal && (
				<PaymentMethodModal
					title={t("common.labels.payment")}
					amountText={amountText}
					recap={modalRecap}
					methods={[{ key: "card" }, { key: "transfer" }]}
					payLabel={t("borrowToken.payAmount", { amount: amountText })}
					onClose={() => setShowMethodModal(false)}
					onPay={handlePay}
				/>
			)}
		</CustomerPageLayout>
	);
};
